import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const DiscoverPlovdivHook = ({ onGameStart }) => {
    const { i18n } = useTranslation();
    const [progress, setProgress] = useState(0);
    const [isRevealed, setIsRevealed] = useState(false);
    const [isHolding, setIsHolding] = useState(false);

    const isHoldingRef = useRef(false);
    const requestRef = useRef();

    const texts = {
        bg: {
            instruction: 'Задръж, за да се върнеш назад във времето',
            title: 'Порталът на времето',
            desc: 'Успешно премина през вековете! Пловдив е град на 7 хълма и хилядолетия съжителстващи култури. От траките и римляните до днешния арт квартал Капана. Готов ли си да се потопиш?',
            btn: 'Опознай града'
        },
        en: {
            instruction: 'Hold to travel back through time',
            title: 'Portal through time',
            desc: 'You have successfully traveled through the centuries! Plovdiv is a city of 7 hills and millennia of coexisting cultures. From the Thracians and Romans to today\'s art district Kapana. Are you ready?',
            btn: 'Discover the City'
        }
    };

    // Масив с епохите, техните диапазони от прогреса (0-100) и пътищата към снимките
    const eras = [
        { id: 'modern', min: 0, max: 15, img: '/assets/plovdiv_modern.jpg', bg: 'Модерен Пловдив', en: 'Modern Plovdiv' },
        { id: 'communism', min: 15, max: 35, img: '/assets/plovdiv_communism.jpg', bg: 'Епоха на Социализма', en: 'Socialist Era' },
        { id: 'revival', min: 35, max: 55, img: '/assets/plovdiv_revival.jpg', bg: 'Българско Възраждане', en: 'National Revival' },
        { id: 'ottoman', min: 55, max: 75, img: '/assets/plovdiv_ottoman.jpg', bg: 'Османско владичество', en: 'Ottoman Rule' },
        { id: 'kingdom', min: 75, max: 90, img: '/assets/plovdiv_kingdom.jpg', bg: 'Средновековно Българско Царство', en: 'Medieval Bulgarian Kingdom' },
        { id: 'roman', min: 90, max: 100, img: '/assets/plovdiv_roman.jpg', bg: 'Римски Филипополис', en: 'Roman Philippopolis' },
        { id: 'thracian', min: 100, max: 102, img: '/assets/plovdiv_thracian.jpg', bg: 'Тракийски Евмолпиас', en: 'Thracian Eumolpias' },
    ];

    const currentLang = i18n.language === 'bg' ? 'bg' : 'en';
    const t = texts[currentLang];

    // Откриване на текущата епоха спрямо стейта на прогреса
    const currentEra = eras.find(e => progress >= e.min && progress < e.max) || eras[eras.length - 1];

    useEffect(() => {
        if (isRevealed) return;

        const updateProgress = () => {
            setProgress(prev => {
                if (isHoldingRef.current) {
                    const next = prev + 0.1; // Намалихме скоростта, за да може туристът да разгледа снимките от епохите
                    if (next >= 100) {
                        setIsRevealed(true);
                        isHoldingRef.current = false;
                        setIsHolding(false);
                        return 100;
                    }
                    return next;
                } else {
                    return prev > 0 ? prev - 2.0 : 0; // Скорост на връщане в днешно време при пускане
                }
            });
            requestRef.current = requestAnimationFrame(updateProgress);
        };

        requestRef.current = requestAnimationFrame(updateProgress);
        return () => cancelAnimationFrame(requestRef.current);
    }, [isRevealed]);

    const startHolding = (e) => {
        if (e.type !== 'touchstart') {
            e.preventDefault();
        }
        isHoldingRef.current = true;
        setIsHolding(true);
    };

    const stopHolding = () => {
        isHoldingRef.current = false;
        setIsHolding(false);
    };

    return (
        <div style={{
            position: 'relative', width: '100%', height: '100vh', maxWidth: '500px', margin: '0 auto',
            overflow: 'hidden', backgroundColor: '#050510', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', color: 'white',
        }}>
            {/* Рендиране на всички фонове със застъпване и opacity анимация */}
            {eras.map(era => (
                <div
                    key={era.id}
                    style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundImage: `url("${era.img}")`,
                        backgroundSize: 'cover', backgroundPosition: 'center',
                        opacity: currentEra.id === era.id ? 1 : 0,
                        transition: 'opacity 0.5s ease-in-out',
                        zIndex: 0
                    }}
                />
            ))}

            {/* Визуален тунелен ефект, който се засилва при задържане */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                boxShadow: `inset 0 0 ${progress * 2.5}px rgba(162, 57, 202, ${0.3 + (progress / 200)})`,
                background: `rgba(15, 5, 25, ${progress / 220})`,
                zIndex: 1, pointerEvents: 'none',
                filter: isHolding ? `blur(${progress / 35}px) hue-rotate(${progress * 1.5}deg)` : 'none',
                transition: 'filter 0.1s linear'
            }} />

            {/* Индикатор за Епохата в горната част на екрана */}
            {!isRevealed && (
                <div style={{
                    position: 'absolute', top: '8%', zIndex: 2, textAlign: 'center', width: '90%',
                    transform: isHolding ? 'scale(1.03)' : 'scale(1)', transition: 'transform 0.3s ease'
                }}>
                    <span style={{
                        fontFamily: '"Cinzel", serif', fontSize: '12px', letterSpacing: '3px',
                        color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: '5px'
                    }}>
                        {currentLang === 'bg' ? 'ПЪТУВАНЕ ВЪВ ВРЕМЕТО' : 'TIME TRAVEL PROGRESS'}
                    </span>
                    <h2 style={{
                        fontFamily: '"Cinzel", serif', fontSize: '24px', color: '#d4af37',
                        textShadow: '0 3px 12px rgba(0,0,0,0.95), 0 0 20px rgba(212,175,55,0.5)',
                        margin: 0, padding: '0 15px', minHeight: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        lineHeight: '1.3'
                    }}>
                        {currentLang === 'bg' ? currentEra.bg : currentEra.en}
                    </h2>
                </div>
            )}

            {/* Пулсиращ златен кръг в центъра */}
            <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: `${progress * 18}px`, height: `${progress * 18}px`,
                backgroundColor: 'rgba(212, 175, 55, 0.12)', borderRadius: '50%',
                filter: 'blur(45px)', zIndex: 1, transition: 'all 0.1s linear', pointerEvents: 'none'
            }} />

            {/* Механизъм на Машината на времето */}
            {!isRevealed && (
                <div style={{ zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '120px' }}>
                    <div
                        onMouseDown={startHolding}
                        onMouseUp={stopHolding}
                        onMouseLeave={stopHolding}
                        onTouchStart={startHolding}
                        onTouchEnd={stopHolding}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        style={{
                            width: '125px', height: '125px', borderRadius: '50%',
                            border: '3px solid #d4af37', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', position: 'relative', cursor: 'pointer',
                            backgroundColor: isHolding ? 'rgba(212, 175, 55, 0.35)' : 'rgba(0,0,0,0.75)',
                            transform: isHolding ? 'scale(0.93)' : 'scale(1)',
                            boxShadow: isHolding ? '0 0 35px rgba(212,175,55,0.7), inset 0 0 15px rgba(212,175,55,0.3)' : '0 12px 30px rgba(0,0,0,0.6)',
                            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)', userSelect: 'none', WebkitUserSelect: 'none'
                        }}
                    >
                        {/* Кръгов SVG прогрес бар */}
                        <svg width="145" height="145" style={{ position: 'absolute', top: -13, left: -13, transform: 'rotate(-90deg)' }}>
                            <circle cx="72.5" cy="72.5" r="67" fill="transparent" stroke="#d4af37" strokeWidth="4"
                                strokeDasharray="421" strokeDashoffset={421 - (421 * progress) / 100}
                                style={{ transition: 'stroke-dashoffset 0.1s linear' }} />
                        </svg>

                        {/* Икона часовник с CSS анимация за въртене наобратно */}
                        <span style={{
                            fontSize: '44px',
                            filter: isHolding ? 'drop-shadow(0 0 15px #d4af37)' : 'none',
                            display: 'inline-block',
                            transformOrigin: 'center center',
                            animation: isHolding ? 'spinBack 3s linear infinite' : 'none'
                        }}>⏳</span>
                        <style>{`@keyframes spinBack { 100% { transform: rotate(-360deg); } }`}</style>
                    </div>
                    <p style={{
                        marginTop: '35px', fontFamily: '"Lora", serif', fontStyle: 'italic', fontSize: '15px',
                        textShadow: '0 2px 6px black', opacity: isHolding ? 0.3 : 0.85, color: '#e5dcd3', transition: 'opacity 0.3s'
                    }}>
                        {t.instruction}
                    </p>
                </div>
            )}

            {/* Финалният прозорец (Модал), появяващ се при достигане на Траките (100%) */}
            <div style={{
                position: 'absolute', zIndex: 3, textAlign: 'center', padding: '40px 25px',
                width: '85%', maxWidth: '400px', background: 'rgba(10, 5, 20, 0.94)',
                border: '2px solid #d4af37', borderRadius: '25px',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.95), 0 0 40px rgba(212, 175, 55, 0.35)',
                backdropFilter: 'blur(12px)', opacity: isRevealed ? 1 : 0,
                transform: isRevealed ? 'scale(1)' : 'scale(0.82)',
                transition: 'all 0.9s cubic-bezier(0.19, 1, 0.22, 1)',
                pointerEvents: isRevealed ? 'auto' : 'none'
            }}>
                <h1 style={{ fontSize: '26px', fontFamily: '"Cinzel", serif', color: '#d4af37', marginBottom: '15px', letterSpacing: '1px' }}>{t.title}</h1>
                <p style={{ fontSize: '15px', fontFamily: '"Lora", serif', color: '#e5dcd3', marginBottom: '35px', lineHeight: '1.65' }}>{t.desc}</p>
                <button onClick={onGameStart} style={{
                    padding: '15px 35px', fontSize: '15px', fontFamily: '"Cinzel", serif',
                    fontWeight: 'bold', color: '#100500', backgroundColor: '#d4af37', border: 'none',
                    borderRadius: '30px', cursor: 'pointer', textTransform: 'uppercase',
                    boxShadow: '0 5px 18px rgba(212, 175, 55, 0.45)', transition: 'all 0.3s ease', letterSpacing: '1px'
                }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.backgroundColor = '#f3cd57';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.backgroundColor = '#d4af37';
                    }}
                >
                    {t.btn}
                </button>
            </div>
        </div>
    );
};

export default DiscoverPlovdivHook;